import { useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

const ACTION_LABELS = {
  client_requested: { label: "Richiesta cliente", color: "var(--br-text)" },
  admin_sent_link: { label: "Link inviato (admin)", color: "var(--br-bronze)" },
  admin_generated_temp: { label: "Temp pwd generata (admin)", color: "var(--br-bronze-soft)" },
  client_consumed_link: { label: "Link consumato", color: "var(--br-parchment)" },
  client_changed_temp: { label: "Temp pwd cambiata", color: "var(--br-parchment)" },
  link_expired: { label: "Link scaduto", color: "var(--br-text-dim)" },
};

export default function AdminPasswordAuditPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/password-reset-audit?limit=200")
      .then(({ data }) => setRows(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="admin-password-audit-page"
      >
        <div className="br-fade-up">
          <div className="br-overline">Sicurezza · Audit</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Password <em>reset audit</em>.
          </h1>
          <p className="text-[color:var(--br-text-dim)] text-[0.95rem] mt-3 max-w-[640px] leading-relaxed">
            Tutte le richieste e le azioni di recupero password effettuate sul
            portale, ordinate dalle più recenti. Conservate per finalità di
            compliance.
          </p>
        </div>

        {error && (
          <div
            className="mt-6 br-card p-4 text-[color:var(--br-red)]"
            data-testid="audit-error"
          >
            {error}
          </div>
        )}

        <div
          className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1"
          data-testid="audit-table"
        >
          <table className="w-full text-left text-[0.88rem]">
            <thead>
              <tr className="border-b border-[color:var(--br-line)]">
                <Th>Quando</Th>
                <Th>Azione</Th>
                <Th>Ruolo</Th>
                <Th>Target</Th>
                <Th>IP</Th>
                <Th>User-Agent</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-[color:var(--br-text-dim)]">
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-8 text-center text-[color:var(--br-text-dim)]"
                    data-testid="audit-empty"
                  >
                    Nessuna richiesta di recupero registrata.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const meta = ACTION_LABELS[r.action] || { label: r.action, color: "var(--br-text)" };
                return (
                  <tr
                    key={r.id}
                    className="border-b border-[color:var(--br-line)]"
                    data-testid={`audit-row-${r.id}`}
                  >
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)] whitespace-nowrap">
                      {formatDate(r.timestamp)}
                    </Td>
                    <Td>
                      <span style={{ color: meta.color }} data-testid={`audit-action-${r.id}`}>
                        {meta.label}
                      </span>
                    </Td>
                    <Td className="font-mono-fin text-[0.78rem] uppercase">
                      {r.actor_role}
                    </Td>
                    <Td className="font-mono-fin text-[0.82rem]">{r.target_email}</Td>
                    <Td className="font-mono-fin text-[0.78rem] text-[color:var(--br-text-dim)]">
                      {r.client_ip || "—"}
                    </Td>
                    <Td className="text-[0.74rem] text-[color:var(--br-text-dim)] max-w-[260px] truncate">
                      {r.user_agent || "—"}
                    </Td>
                    <Td className="text-[0.78rem] text-[color:var(--br-text-dim)]">
                      {r.note || "—"}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children }) {
  return (
    <th className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium text-[color:var(--br-text-dim)]">
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 ${className}`}>{children}</td>;
}
